import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Region-level driver recruitment pipeline configuration.
 */
export const schema = z
  .object({
    funnel_open: z
      .boolean()
      .describe(
        'Whether the driver recruitment funnel is open for this region. When true, new drivers can be recruited.'
      )
      .optional(),
    active_driver_target: z
      .number()
      .int()
      .nonnegative()
      .describe('Target number of active drivers for this region. Used to determine recruitment goals.')
      .optional(),
    waitlist_target: z
      .number()
      .int()
      .nonnegative()
      .describe('Target number of drivers on the waitlist for this region. Maintains a pipeline of ready drivers.')
      .optional(),
    backlog_target: z
      .number()
      .int()
      .nonnegative()
      .describe('Maximum number of drivers allowed in the backlog. When reached, recruitment funnel may be closed.')
      .optional(),
    engagement_frequency_days: z
      .number()
      .int()
      .positive()
      .describe(
        'How often (in days) to engage with drivers in the pipeline. Used for scheduling follow-up communications.'
      )
      .optional(),
    max_missed_confirmations: z
      .number()
      .int()
      .nonnegative()
      .describe(
        'Maximum number of missed confirmations allowed before removing a driver from the recruitment pipeline.'
      )
      .optional(),
  })
  .strict()
  .describe('Driver recruitment pipeline configuration for managing funnel state, targets, and engagement rules.');

export type DriverPipeline = z.infer<typeof schema>;

export const definition = define({
  key: 'driver_pipeline',
  kind: DEFINITION_KINDS.SETTING,
  schema,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN],
  label: 'Driver Pipeline',
  description: 'Driver recruitment pipeline configuration with funnel management and engagement settings.',
  category: CATEGORIES.REGION_PIPELINE.key,
  storage: {
    // completely unneccessary since jsonb config column storage is the default
    levels: {
      region: {
        type: 'jsonb',
        path: 'driver_pipeline',
      },
    },
  },
  defaultValue: {
    funnel_open: false,
    active_driver_target: 20,
    waitlist_target: 25,
    backlog_target: 50,
    engagement_frequency_days: 7,
    max_missed_confirmations: 3,
  },
});
